import $models from '@/models'
import moment from '@/moment-gp.js'
import util from '@/util'
import clonedeep from 'lodash.clonedeep'
import { flatten_sequence } from '@/models/sequence/util'

const log1 = (...args) => { /* console.log(...args) */ }
const log2 = (...args) => { /* console.log(...args) */ }
const log3 = (...args) => { console.log("[OPTIMIZER:GETTER]", ...args) }

export default {
  // optimization_state: state => state.optimizer_state,
  optimizer_result_data: state => state.optimizer_result,
  optimizer_user_error: state => state.optimizer_user_error,
  optimizer_manual_dayplan: state => state.optimizer_manual_dayplan,
  optimizer_response_time: state => state.optimizer_response_time,
  optimizer_planning_horizon: state => state.optimizer_planning_horizon,
  optimizer_finish_dates_tasks: state => state.optimizer_result.finishDatesTasks || {},
  optimizer_finish_dates_commitments: state => state.optimizer_result.finishDatesCommmitments || {},
  optimization_running: state => state.optimizer_state === 'running',

  /* ############################# OPTIMIZER INPUT ################################## */

  /* ---------------------------- get related goals  -------------------------------- */
  optimizer_input_related_goal_list: (state, getters) => {
    log3("optimizer_input_related_goal_list")

    /* get commitment list */
    let commitment_list = $models.commitment.getters.commitment_list_all_optimizer;
    let related_goal_list = {}

    commitment_list.forEach(commitment => {
      related_goal_list[ commitment.goalId ] = true;
    })

    return related_goal_list
  },

  /* ----------------------------- get commitments ---------------------------------- */
  optimizer_input_commitments: (state, getters) => {
    log3("optimizer_input_commitments")

    /* get commitment list */
    let commitment_list = $models.commitment.getters.commitment_list_all_optimizer;
    let related_goal_list = {}

    /* Pre-sort commitments by timeslot id so it is easier to attach them later on */
    let commitments_by_tsc_id_map = {}
    let tsc_id_by_commitment_id_map = {}

    commitment_list.forEach(commitment => {
      let tsc_id = commitment.user.timeslotCollectionId || null
      if ( !commitments_by_tsc_id_map[ tsc_id ] ) {
        commitments_by_tsc_id_map[ tsc_id ] = [];
      }

      /* Careful: If commitment.endDate is undefined that means that
         moment(undefined) becomes the current timestamp */
      var commitmentData = {
        ...commitment,
        from: commitment.startDate ? commitment.startDate.toOptimizer() : null,
        to:   commitment.endDate   ? commitment.endDate.toOptimizer()   : null,
      };
      delete commitmentData['startDate']
      delete commitmentData['endDate']

      commitments_by_tsc_id_map[ tsc_id ].push(commitmentData);
      related_goal_list[ commitment.goalId ] = true;
      tsc_id_by_commitment_id_map[ commitment.commitmentId ] = tsc_id; // (null = unassigned)
    })

    log1("tsc_id_by_commitment_id_map", tsc_id_by_commitment_id_map);

    log2(
      "[optimizer:input] commitment list debug",
      commitment_list,
      commitment_list.map(item => item.startDate.format()),
      commitment_list.map(item => item.endDate.format())
    );

    return { commitment_list, commitments_by_tsc_id_map, related_goal_list, tsc_id_by_commitment_id_map }
  },

  /* ------------------------ existing events ----------------------------- */
  optimizer_input_existing_events: (state, getters) => {
    log3("optimizer_input_existing_events")

    const { related_goal_list, tsc_id_by_commitment_id_map } = getters.optimizer_input_commitments
    let results = Object.keys( related_goal_list ).map(goalId => {
      let existing_events_from_db = $models.commitmentSession
        .with({ goalId })
        .getList()

      if ( existing_events_from_db === null ) {
        existing_events_from_db = []
      } else {
        existing_events_from_db = existing_events_from_db.itemsAsArray().sort(util.sorter.by_startedAt);
        let nrExisting_events_from_db = existing_events_from_db.length
        for (let i = 0; i < nrExisting_events_from_db; i++) {
          Object.assign(existing_events_from_db[i], { type: 'manual' })
        }
        //console.log('existing_events_from_db', existing_events_from_db)
      }

      // get recorded session for goalId
      let allRecordedSessions =  $models.taskRecordedSession.getters.get_recorded_sessions_all_goals
      let recordedSessions = allRecordedSessions[goalId]
      if (recordedSessions) {

        // include all recorded session from today
        let user_timezone = $models.userSettings.getters.user_get_timezone;
        let formatedDateToday = moment.tz(user_timezone).startOf('day').format()
        let timestampStartDay = moment(formatedDateToday).unix()
        existing_events_from_db = existing_events_from_db.concat(recordedSessions
          .filter(recordedSession => parseInt(recordedSession.$id) + recordedSession.$state.duration > timestampStartDay)
          .map(recordedSession => {
            let type = parseInt(recordedSession.$id) + recordedSession.$state.duration > moment.utc().unix() ? 'recordedActive' : 'recorded'
            return clonedeep({
              $state: {
                commitmentId: recordedSession.$state.commitmentId,
                from: parseInt(recordedSession.$id),
                to: parseInt(recordedSession.$id) + recordedSession.$state.duration
              },
              $id: recordedSession.$id,
              commitmentId: recordedSession.$state.commitmentId,
              tasks: [
                { taskId: recordedSession.$state.taskId, duration: recordedSession.$state.duration }
              ],
              type: type
            })
        }))
      }
      // TODO: Handle no events
      let existing_events_mapped = existing_events_from_db.map(event => {
        return clonedeep({
          ...event.$state,
          id: event.$id,
          timeslotCollectionId: tsc_id_by_commitment_id_map[ event.commitmentId ],
          tasks: event.tasks,
          type: event.type || null
        })
      })

      //console.log('existing_events_mapped', existing_events_mapped)
      //console.log('existing_events_mapped', existing_events_mapped)
      return existing_events_mapped
    })

    let existing_events_by_tsc_id = {};

    [].concat(...results).forEach(event => {
      if ( !event ) { return }
      let tscId = event.timeslotCollectionId

      if ( !( tscId in existing_events_by_tsc_id ) ) {
        existing_events_by_tsc_id[ tscId ] = []
      }
      existing_events_by_tsc_id[ tscId ].push(event)
    })

    log1('existing events: ', existing_events_by_tsc_id)
    return existing_events_by_tsc_id
  },

  /* ------------------ get timeslots and commitments --------------------- */
  optimizer_input_tsc_data: (state, getters, rootState, rootGetters) => {
    log3("optimizer_input_tsc_data")

    /* Get timeslots from worker result */
    let timeslot_flat_by_tsc = rootGetters['timeslots/get_timeslot_flat_cache']
    /* get all calendar events */
    // const tscList = rootGetters['timeslots/timeslot_collection_list'];
    const tscList = $models.timeslotCollection.getList().itemsAsArray();
    const { commitments_by_tsc_id_map } = getters.optimizer_input_commitments

    let tsc_data_array = [];
    Object.keys(tscList).forEach((timeslot_collection_idx) => {

      let timeslot_collection = tscList[timeslot_collection_idx];
      let timeslot_collection_id = timeslot_collection.$id;
      let timeslots_flat = timeslot_flat_by_tsc[timeslot_collection_id];

      log2("[optimizer:input] timeslot_flat_by_tsc", timeslot_flat_by_tsc);
      log2("[optimizer:input] timeslot_collection_id", timeslot_collection_id);
      log2("[optimizer:input] timeslots_flat", timeslots_flat);

      if ( !timeslots_flat ) {
        console.log("[optimizer:input] timeslot_flat_by_tsc not ready", timeslot_collection_id)
        return
      }

      // TODO: Sort by prioidx

      // Default is 'COMMITMENT' - can be removed in the future
      let prioritized_effort_type = timeslot_collection.prioritized_effort_type || 'COMMITMENT';
      let tsc_color = timeslot_collection.color

      /* At this point we convert the date and time of a timeslot to a
        'timezoned' moment object. This is necessary because timeslots are
        neutral (and should be ideally saved as utc). They are not neutral
        anymore as soon as they get passed to the optimizer. The reason
        for this is that the optimizer needs UTC inputs, but timeslots
        are "timezone neutral". In order to be able to plan, the current
        user timezone is 'appended' to the "timezone neutral" datetime, so

        '2018-05-06T14:00' + 'America/New_York' becomes
        '2018-05-06T14:00:00-04:00'.

        However, this is ONLY TRUE for the optimizer input. It is NOT TRUE
        if we want to display timeslots in a fullcalendar. That's why we do
        conversion here and not in flattened_calendar_data_by_timeslot_collection_id.

        7.1.2018: This is not the case anymore. TODO: Verify what has changed.
      */

      let timeslots_mapped = timeslots_flat.event_data.map( event => {
        // let fromMoment = moment.fromTimezoneNeutral( event.from );
        // let toMoment   = moment.fromTimezoneNeutral( event.to );

        /* Skip deleted timeslots */
        /*if ( event.deleted ) {
          return;
        }*/

        return {
          // from: fromMoment.unix(),
          // to:   toMoment.unix(),
          from: moment(event.from * 1000).toOptimizer(),
          to:   moment(event.to * 1000).toOptimizer(),
          rank: event.rank || -1
        };
      })//.filter(event => event)

      // log2('[optimizer:input] timeslot_collection_idx:', timeslot_collection_idx)
      /*
      let nrElementsInEffort_list = commitment_list.length
      let commitments_mapped = new Array(nrElementsInEffort_list)
      let nrEffortsAdded = 0
      for (let i = 0; i < nrElementsInEffort_list; i++) {
        let commitment = commitment_list[i]
        if (commitment.user.timeslot_collection_id === timeslot_collection_id) {

          // Careful: If commitment.endDate is undefined that means that
          // moment(undefined) becomes the current timestamp
          var commitmentData = {
            ...commitment,
            from: commitment.startDate ? commitment.startDate.toOptimizer() : null,
            to: commitment.endDate ? commitment.endDate.toOptimizer() : null,
          };
          delete commitmentData['startDate']
          delete commitmentData['endDate']
          commitments_mapped[nrEffortsAdded] = commitmentData;
          nrEffortsAdded += 1
        }
      }
      commitments_mapped = commitments_mapped.slice(0,nrEffortsAdded)
      */

      let commitments_mapped = commitments_by_tsc_id_map[ timeslot_collection_id ] || []

      if ( commitments_mapped.length === 0 && timeslots_mapped.length === 0 ) {
        log2("[optimizer:input] Skipping", timeslot_collection_id);
      } else {
        tsc_data_array.push({
          prioritizedEffortType:  prioritized_effort_type,
          timeslotCollectionId:   timeslot_collection_id,
          timeslots:              timeslots_mapped,
          efforts:                commitments_mapped.sort(util.sorter.by_rank), // TODO: "efforts"
          rank:                   timeslots_flat.rank,
          color:                  tsc_color
        })
      }
    });

    /*
    let goal_config_list = {};
    this.$refs.config.forEach((configurator, idx) => {
      // if ( idx !== 1 ) { return {}; } // HACK

      let goal_config = configurator.exportFlatWeekdayList( this.planning_horizon );
      let project_id = configurator.project_id;

      let project_info = this.project_list_with_details[project_id];
      if ( !project_info.deleted ) {
        goal_config_list[project_id] = goal_config;
      }
    })
    */

    // log1('returning promise with tsc_data_array', tsc_data_array)
    return tsc_data_array
  },

  /* ---------------------------- get sequences -------------------------------- */
  optimizer_input_sequences: (state, getters) => {
    log3("optimizer_input_sequences")
    // const sequence_list = $models.sequence.subscribeList();
    // const promiseSequences = sequence_list.$promise;

    const { commitment_list } = getters.optimizer_input_commitments

    let results = commitment_list.map(commitment => {
      if ( commitment.type !== 'SEQUENCE' ) {
        return null
      }
      let sequenceId = commitment.sequenceId;
      console.log("[optimizer_input_sequences]", sequenceId)

      if ( !sequenceId ) {
        console.log("[optimzer:input] sequence not connected", sequenceId)
        return null;
      }

      let sequenceModel = $models.sequence.getNode( sequenceId );

      // TODO: repeat
      // TODO: local override
      // TODO: local repeat override
      // TODO: try catch ... task might be not accessible
      if ( sequenceModel === null || !sequenceModel.$state.sequenceItemList ) {
        console.log("[optimzer:input] sequence not ready", sequenceId)
        return null
      }

      let flat_sequence = flatten_sequence( sequenceModel.$state.sequenceItemList, /*false*/ );

      flat_sequence.forEach(sequenceItem => {
        if ( sequenceItem.type === "SESSION:TASK" ) {

          if ( !sequenceItem.goalId || !sequenceItem.memberId || !sequenceItem.taskId) {
            console.warn("Found sequence with SESSION:TASK, but no task was assigned -> skipping");
            return
          }

          let task = $models.task
            .with({ goalId: sequenceItem.goalId, memberId: sequenceItem.memberId })
            .getNodeFull( sequenceItem.taskId )

          if ( task === null ) {
            console.log("[optimzer:input] task not ready", sequenceItem.taskId )
            return
          }

          sequenceItem.durationRemaining = task.durationRemaining
          sequenceItem.name = task.name;
          sequenceItem._debug_task = clonedeep( task.$state );
        }
      })

      return {
        commitmentId: commitment.commitmentId,
        sequenceId: sequenceId,
        sequence: flat_sequence,
      };
    })

    let sequences_by_id = {};

    results.filter(r => r).forEach(sequence_result => {
      if ( sequence_result ) {
        sequences_by_id[ sequence_result.sequenceId ] = sequence_result.sequence
      }
    })

    log1("[optimizer:input] sequences_by_id", sequences_by_id);
    return sequences_by_id;
  },

  /* ----------------------------- get all tasks ------------------------------- */
  optimizer_input_tasks: (state, getters, rootState, rootGetters) => {
    log3("optimizer_input_tasks")

    const { related_goal_list } = getters.optimizer_input_commitments
    log1("[optimizer:input] related_goal_list", related_goal_list);

    let tasks_by_goal_id_and_commitment_id = {}
    Object.keys( related_goal_list ).forEach(goalId => {

      tasks_by_goal_id_and_commitment_id[ goalId ]
        = $models.task.with({ goalId }).getters.lane_task_list_current_user

      // $models.task.with({ goalId }).get_lane_task_list(true) // Get only tasks for current user

      /* "zip" results */
      // TODO: Maybe not necessary
      /* We need to call get_lane_task_list again, to get the results! */
      /*
      let map2 = {}
      Object.keys( related_goal_list ).forEach((goalId, idx) => {
        map2[ goalId ] = $models.task
                        .with({ goalId })
                        .get_lane_task_list(true) // Get only tasks for current user
      });
      log1("[optimizer:input] task list: map2", map2);
      */
    })

    log1("[optimizer:input] task list: map", tasks_by_goal_id_and_commitment_id);
    return tasks_by_goal_id_and_commitment_id;
  },

   /* ----------------------------- get all committedTimeDaily setting  ------------------------------- */
  optimizer_input_commitment_settings: (state, getters, rootState, rootGetters) => {
    log3("optimizer_input_commitment_settings")

    const { related_goal_list } = getters.optimizer_input_commitments
    log1("[optimizer:input] related_goal_list", related_goal_list);

    let commitment_settings_by_goal_id_and_commitment_id = {}
    Object.keys( related_goal_list ).forEach(goalId => {

      commitment_settings_by_goal_id_and_commitment_id[ goalId ]
        = $models.commitment.with({ goalId }).getters.commitment_settings_current_user
    })

    log1("[optimizer:input] commitment_settings_by_goal_id", commitment_settings_by_goal_id_and_commitment_id);
    return commitment_settings_by_goal_id_and_commitment_id;
  },

  /* ---------------------------------- merge ------------------------------------ */
  /*
   * Generate Optimizer Input
   * ? TODO: Start later when view is changed in calendar
   */
  optimizer_input: (state, getters, rootState, rootGetters) => {
    log3("optimizer_input")

    // const {
    //   commitment_list,
    //   commitments_by_tsc_id_map,
    //   related_goal_list,
    //   tsc_id_by_commitment_id_map
    // } = getters.optimizer_input_commitments

    const existing_events_by_tsc_id = getters.optimizer_input_existing_events
    const tsc_data_array = getters.optimizer_input_tsc_data
    const sequences_by_id = getters.optimizer_input_sequences
    const tasks_by_goal_id_and_commitment_id = getters.optimizer_input_tasks
    const commitment_settings_by_goal_id_and_commitment_id = getters.optimizer_input_commitment_settings
    const optimizer_manual_dayplan = getters.optimizer_manual_dayplan
    console.log('optimizer_manual_dayplan', optimizer_manual_dayplan)

    tsc_data_array.forEach(tsc_data => {

      /* assign existing events */
      if (tsc_data.timeslotCollectionId && existing_events_by_tsc_id[ tsc_data.timeslotCollectionId ]) {
        tsc_data.existingEvents = existing_events_by_tsc_id[ tsc_data.timeslotCollectionId ]
      } else {
        tsc_data.existingEvents = []
      }

      /* handle different commitment types */
      tsc_data.efforts.forEach(commitment => { // TODO: "efforts"
        if ( commitment.type === 'SEQUENCE' ) {
          let sequenceId = commitment.sequenceId;
          if ( !( sequenceId in sequences_by_id ) ) {
            throw new Error('Sequence not found: ' + sequenceId) // Can be removed or not
          }
          commitment.sequence = sequences_by_id[ sequenceId ]
        }
        if ( commitment.type === 'TASKLIST'
          && tasks_by_goal_id_and_commitment_id[ commitment.goalId ]
          && tasks_by_goal_id_and_commitment_id[ commitment.goalId ][ commitment.commitmentId ]) {
          let tasklist = tasks_by_goal_id_and_commitment_id[ commitment.goalId ][ commitment.commitmentId ];
          if ( tasklist ) {
            commitment.tasks = tasklist.filter(task => {
              return !task.deleted && !task.done
            }).map(task => {
              return {
                taskId: task.$id,
                durationRemaining: task.durationRemaining,
                commitmentId: commitment.commitmentId, // TODO: remove after debugging
                name: task.name // TODO: remove after debugging
              }
            });
          } else {
            commitment.tasks = null;
          }
        }

        commitment.committedTimeDaily = commitment_settings_by_goal_id_and_commitment_id[ commitment.goalId ][ commitment.commitmentId ];
        if ( commitment.type === 'MAGICLIST' ) {
          console.warn("[optimizer:input] Got magic list - this is not implemented");
          // TODO
        }
        if ( commitment.type === 'BACKLOG' ) {
          /* Remove backlogs for now... later useful for magic blocks */
          commitment = {} // TODO: Testen
        }
      })
    })

    tsc_data_array['DAYPLAN'] = optimizer_manual_dayplan // HACK

    return tsc_data_array;
  },

  /* ############################# OPTIMIZER RESULTS ################################## */

  /* --------------------------- mapped_optimizer_results ----------------------------- */

  mapped_optimizer_results: (state, getters, rootState, rootGetters) => {
    //console.log('## Triggered mapped_optimizer_results (pseudoIds are invalid now!)');
    if ( !getters.optimizer_result_data ) {
      return [];
    }
    if ( !( 'plan' in getters.optimizer_result_data ) ) {
      return [];
    }

    let visible_range = rootGetters["calendar/calendar_view_settings"].visible_range;
    let from_timestamp = visible_range.from_date.unix();
    let to_timestamp = 99999999999999// visible_range.to_date.unix();

    //console.log("[optimizer] Filtering optimizer events with start", visible_range.from_date.format(), "to", visible_range.to_date.format());

    // Filter events to increase performance
    //let eventlist = getters.optimizer_result_data.plan.filter(event => event.from > from_timestamp  && event.from < to_timestamp).map((item) => {
    let eventlist = getters.optimizer_result_data.plan
      .filter(item => {
        /* Here we're still dealing with timestamp. Conversion to moment happens in the map-step */
        return item.from > from_timestamp - 86400 && item.from < to_timestamp + 86400
      }).map((item) => {
      /*if ( !isNaN( item.day ) ) {
        // Day is number or can be understand as a number (e.g. '12').
        //   Add the number to today date
        date = moment(today).add(item.day, 'd').format('YYYY-MM-DD');
        // TODO: Solve time zone issue / Fix date function => timezone
      } else {
        // item.day already has the format 'YYYY-MM-DD'
        date = item.day;
      }*/

      // console.log('item', item)
      if (item.type === 'TASKLIST' ) {
        return {
          type:           item.type || '',
          source:         item.source,
          commitmentSessionId:  item.commitmentSessionId,
          commitmentId:   item.commitmentId,
          start:          moment.fromOptimizer( item.from ), // moment.unix( item.from ).tz( user_timezone ), // TODO
          end:            moment.fromOptimizer( item.to ), // moment.unix( item.to ).tz( user_timezone ), // TODO
          //devMessageFrom: item.devMessageFrom,
          //devMessageTo:   item.devMessageTo,
          title:          item.title,
          tasks:          item.tasks,
          pseudoId:       util.get_new_uuid()
        };
      } else if (item.type === 'SEQUENCE' ) {
        return {
          type:           item.type || '',
          source:         item.source,
          commitmentSessionId:  item.commitmentSessionId,
          commitmentId:   item.commitmentId,
          start:          moment.fromOptimizer( item.from ), // moment.unix( item.from ).tz( user_timezone ), // TODO
          end:            moment.fromOptimizer( item.to ), // moment.unix( item.to ).tz( user_timezone ), // TODO
          title:          item.title,
          sequenceItems: item.sequenceItems,
          pseudoId:       util.get_new_uuid(),
        };
      }
    });
    return eventlist;
  },

  /* --------------------------- masked_optimizer_results ----------------------------- */

  masked_optimizer_results: (state, getters) => {

    console.log('## Triggered masked_optimizer_results');

    let maskedIds = Object.values( state.masked_event_ids );
    // console.log("Masked ids:", maskedIds );
    //console.log('session in getter 1', getters.mapped_optimizer_results)
    return getters.mapped_optimizer_results.filter(item => {
      return !maskedIds.includes( item.pseudoId )
    });
  },

  optimizer_sessions_filtered: (state, getters) => {
    if (state.optimizer_result && state.optimizer_result.sessionItems) {
      return state.optimizer_result.sessionItems.filter(session => session.key)
    } else {
      return []
    }
  },

  optimizer_active_session_id: (state, getters) => {
    if (state.optimizer_result && state.optimizer_result.activeSessionId !== null) {
      return state.optimizer_result.activeSessionId
    } else {
      return 0
    }
  }

}
